// 5 micro-learning modules for Prompt Engineering
// Each module has short "cards" (concept), quizzes, scenario challenges, and a recap.

export const MODULES = [
  {
    id: "m1",
    number: "01",
    title: "Prompt Basics",
    subtitle: "The anatomy of a great instruction",
    duration: "8 min",
    color: "primary",
    cover:
      "https://images.unsplash.com/photo-1631125916276-69bcd14e3980?auto=format&fit=crop&w=1200&q=70",
    badge: "First Draft",
    intro:
      "A prompt is a set of instructions. In this module you'll learn the four ingredients every strong prompt shares: role, task, context, and format.",
    lessons: [
      {
        id: "m1-c1",
        type: "concept",
        title: "What is a prompt?",
        body:
          "A prompt is the input you give a language model. Better inputs produce better outputs. Think of it less like a search query, more like briefing a smart intern who reads exactly what you write — nothing more, nothing less.",
        takeaway: "Words in → behavior out. Prompt quality is engineering, not luck.",
      },
      {
        id: "m1-c2",
        type: "concept",
        title: "The R.T.C.F framework",
        body:
          "Every strong prompt has four parts. Role: who the model should be. Task: what to do. Context: relevant background. Format: how to present the answer. Skip any and results turn generic.",
        takeaway: "Role · Task · Context · Format — the four legs of a stable prompt.",
      },
      {
        id: "m1-q1",
        type: "quiz",
        title: "Which part is missing?",
        question:
          'Prompt: "Write a summary of the meeting notes below."',
        options: [
          { id: "a", label: "Role", correct: true },
          { id: "b", label: "Task", correct: false },
          { id: "c", label: "Context", correct: false },
          { id: "d", label: "Format", correct: true },
        ],
        multi: true,
        explain:
          "There is a task, and context (meeting notes) will be added. But there is no role assigned and no output format specified.",
      },
      {
        id: "m1-s1",
        type: "scenario",
        title: "The customer email",
        scenario:
          "You need to write a prompt that helps a support agent draft a refund reply to a frustrated customer. The reply should be empathetic, under 120 words, and end with a next step.",
        rubric: [
          "Assigns a clear role (e.g. support agent, empathetic tone)",
          "States the task and constraints (under 120 words, next step)",
          "Includes format instructions (paragraph, no bullet lists)",
        ],
        hint: "Try: 'You are an empathetic support agent...' + constraints + format.",
      },
      {
        id: "m1-recap",
        type: "recap",
        title: "Recap: Prompt Basics",
        points: [
          "Prompts are engineered, not guessed.",
          "R.T.C.F: Role, Task, Context, Format.",
          "Constraints (length, tone, format) sharpen output.",
        ],
      },
    ],
  },

  {
    id: "m2",
    number: "02",
    title: "Zero-Shot & Few-Shot",
    subtitle: "Teaching by example, or not at all",
    duration: "10 min",
    color: "primary",
    cover:
      "https://images.unsplash.com/photo-1486718448742-163732cd1544?auto=format&fit=crop&w=1200&q=70",
    badge: "Pattern Setter",
    intro:
      "Sometimes the model just needs an instruction (zero-shot). Sometimes it needs a few examples to lock onto a pattern (few-shot). You'll learn when each wins.",
    lessons: [
      {
        id: "m2-c1",
        type: "concept",
        title: "Zero-shot prompting",
        body:
          "You describe the task, no examples given. Fast, cheap, and works for tasks the model already understands well. Fails when the desired output style is unusual.",
        takeaway: "Zero-shot = pure instruction. Best for common, well-known tasks.",
      },
      {
        id: "m2-c2",
        type: "concept",
        title: "Few-shot prompting",
        body:
          "You show the model 2–5 input→output examples that model the exact behavior you want. Especially powerful for custom formats, tone, and edge-case handling.",
        takeaway: "Few-shot = show, don't just tell. 3 examples usually beats 30 words of description.",
      },
      {
        id: "m2-q1",
        type: "quiz",
        title: "When would few-shot help most?",
        question: "Which task most benefits from few-shot examples?",
        options: [
          { id: "a", label: "Translating English to French", correct: false },
          { id: "b", label: "Classifying support tickets into your company's custom labels", correct: true },
          { id: "c", label: "Summarising an article in 3 bullets", correct: false },
          { id: "d", label: "Writing a haiku", correct: false },
        ],
        multi: false,
        explain:
          "Custom label sets are unusual to the model, so a few labelled examples anchor the behavior far better than description alone.",
      },
      {
        id: "m2-s1",
        type: "scenario",
        title: "Classify feedback",
        scenario:
          "You want the model to classify user feedback into one of: 'bug', 'feature request', 'compliment', or 'confusion'. Write a few-shot prompt with 3 examples so the model returns just the label.",
        rubric: [
          "Includes 3+ example input→label pairs",
          "Uses a strict output format (single label, no prose)",
          "Covers at least 3 of the 4 label categories in the examples",
        ],
        hint: "Structure like:\nInput: '...'\nLabel: bug\n\nInput: '...'\nLabel: feature request\n...",
      },
      {
        id: "m2-recap",
        type: "recap",
        title: "Recap: Zero & Few-Shot",
        points: [
          "Zero-shot for common tasks; few-shot for custom patterns.",
          "3 examples usually enough. Keep them varied.",
          "Fix output shape by fixing example shape.",
        ],
      },
    ],
  },

  {
    id: "m3",
    number: "03",
    title: "Chain-of-Thought",
    subtitle: "Making the model show its work",
    duration: "9 min",
    color: "primary",
    cover:
      "https://images.pexels.com/photos/33860172/pexels-photo-33860172.jpeg?auto=compress&cs=tinysrgb&w=1200",
    badge: "Reasoner",
    intro:
      "For math, logic, and multi-step problems, asking the model to reason step-by-step dramatically improves accuracy. Learn when — and when not — to ask for reasoning.",
    lessons: [
      {
        id: "m3-c1",
        type: "concept",
        title: "Why step-by-step works",
        body:
          "Language models predict one token at a time. Reasoning out loud gives the model 'scratch space' to compute intermediate results before committing to an answer — much like humans thinking on paper.",
        takeaway: "Ask for reasoning when problems have multiple steps. Skip it for lookups.",
      },
      {
        id: "m3-c2",
        type: "concept",
        title: "Phrases that trigger reasoning",
        body:
          "'Let's think step by step.' 'First, identify... Then...' 'Break the problem into parts, then answer.' These prompts flip the model into explicit-reasoning mode.",
        takeaway: "Explicit reasoning cues → measurably better answers on hard tasks.",
      },
      {
        id: "m3-q1",
        type: "quiz",
        title: "When is Chain-of-Thought overkill?",
        question: "Which task probably does NOT need step-by-step reasoning?",
        options: [
          { id: "a", label: "'What is the capital of Portugal?'", correct: true },
          { id: "b", label: "Multi-step word problem", correct: false },
          { id: "c", label: "Debugging a code snippet's logic", correct: false },
          { id: "d", label: "Ranking 5 options by 3 criteria", correct: false },
        ],
        multi: false,
        explain: "Simple factual recall doesn't benefit from reasoning steps and just wastes tokens.",
      },
      {
        id: "m3-s1",
        type: "scenario",
        title: "The pricing puzzle",
        scenario:
          "A customer bought 3 items at $12.50, got a 15% discount, then paid 8% tax. Write a prompt that guides the model to compute the total step-by-step and clearly state the final amount.",
        rubric: [
          "Asks for step-by-step reasoning explicitly",
          "Specifies output format (steps then final total)",
          "Constrains the answer to end with a single final number",
        ],
        hint: "Include 'Show your work step by step' and 'end with: Final total: $X.XX'.",
      },
      {
        id: "m3-recap",
        type: "recap",
        title: "Recap: Chain-of-Thought",
        points: [
          "Use CoT for multi-step math, logic, and analysis.",
          "'Let's think step by step' is a real technique.",
          "Constrain the final answer format so it's easy to parse.",
        ],
      },
    ],
  },

  {
    id: "m4",
    number: "04",
    title: "Role & Persona",
    subtitle: "Who the model pretends to be",
    duration: "7 min",
    color: "primary",
    cover:
      "https://images.unsplash.com/photo-1618004652321-13a63e576b80?auto=format&fit=crop&w=1200&q=70",
    badge: "Impersonator",
    intro:
      "Giving the model a persona shifts vocabulary, depth, and tone. This module shows how to use roles for expertise, empathy, and evaluation.",
    lessons: [
      {
        id: "m4-c1",
        type: "concept",
        title: "Roles set expectations",
        body:
          "'You are a senior tax accountant with 20 years of experience.' — the model now leans into technical vocabulary, precision, and disclaimers. Roles are cheap but powerful priors.",
        takeaway: "Role = expertise + tone in one line.",
      },
      {
        id: "m4-c2",
        type: "concept",
        title: "Personas for evaluation",
        body:
          "Use roles to critique your own work: 'You are a skeptical VP of Engineering. Poke holes in this proposal.' Then swap the role to strengthen the weaknesses found.",
        takeaway: "Adversarial personas surface blind spots faster than solo thinking.",
      },
      {
        id: "m4-q1",
        type: "quiz",
        title: "Best role for this task?",
        question: "You need feedback on a beginner cooking recipe. Which role helps most?",
        options: [
          { id: "a", label: "You are a Michelin-star chef", correct: false },
          { id: "b", label: "You are a busy parent cooking on weeknights", correct: true },
          { id: "c", label: "You are a food chemist", correct: false },
          { id: "d", label: "You are an AI assistant", correct: false },
        ],
        multi: false,
        explain:
          "Match the role to your actual audience. A Michelin chef's feedback wouldn't reflect a beginner's kitchen.",
      },
      {
        id: "m4-s1",
        type: "scenario",
        title: "Explain like I'm 10",
        scenario:
          "You want the model to explain how HTTPS works to a 10-year-old. Design a prompt using a role that guarantees warm, simple, non-technical language and includes one everyday analogy.",
        rubric: [
          "Assigns a teacher/storyteller-style role for a child",
          "Constrains vocabulary and length appropriately",
          "Explicitly requests an everyday analogy",
        ],
        hint: "'You are a friendly teacher speaking to a curious 10-year-old...'",
      },
      {
        id: "m4-recap",
        type: "recap",
        title: "Recap: Role & Persona",
        points: [
          "Roles adjust tone, vocabulary, and depth for free.",
          "Match the role to the audience, not to the topic.",
          "Adversarial roles = built-in QA.",
        ],
      },
    ],
  },

  {
    id: "m5",
    number: "05",
    title: "Advanced Techniques",
    subtitle: "RAG, guardrails, and evaluation",
    duration: "12 min",
    color: "primary",
    cover:
      "https://images.unsplash.com/photo-1465101178521-c1a9136a3b99?auto=format&fit=crop&w=1200&q=70",
    badge: "Prompt Architect",
    intro:
      "Production prompts need more than instructions. Learn retrieval-augmented generation basics, guardrails to prevent misuse, and how to measure prompt quality.",
    lessons: [
      {
        id: "m5-c1",
        type: "concept",
        title: "Retrieval-Augmented Generation",
        body:
          "RAG means: fetch relevant documents from a knowledge base first, then include them in the prompt as context. This lets a model answer from your data, not just its training memory.",
        takeaway: "RAG = your data + the model's language ability, combined.",
      },
      {
        id: "m5-c2",
        type: "concept",
        title: "Guardrails",
        body:
          "Guardrails are constraints that prevent unwanted behavior: refusing off-topic questions, avoiding personal opinions, staying within a knowledge scope. Add them explicitly in the system prompt.",
        takeaway: "State what the model must NOT do, not just what it should.",
      },
      {
        id: "m5-c3",
        type: "concept",
        title: "Evaluating a prompt",
        body:
          "Great prompt engineers test prompts on 10+ varied inputs, tag each output good/bad, and iterate. Change one thing at a time. Save prompt versions like code.",
        takeaway: "Prompts are code. Version them. Test them. Refactor them.",
      },
      {
        id: "m5-q1",
        type: "quiz",
        title: "Guardrail phrasing",
        question: "Which is the strongest guardrail?",
        options: [
          { id: "a", label: "'Try to only answer about cooking.'", correct: false },
          { id: "b", label: "'If the user asks about anything other than cooking, respond with: \"I can only help with cooking topics.\"'", correct: true },
          { id: "c", label: "'Please stay on topic.'", correct: false },
          { id: "d", label: "'Be helpful.'", correct: false },
        ],
        multi: false,
        explain:
          "Strong guardrails specify the exact behavior AND the fallback text verbatim.",
      },
      {
        id: "m5-s1",
        type: "scenario",
        title: "Design a production prompt",
        scenario:
          "You're building a customer-support bot that only answers questions about a specific SaaS product using retrieved help-doc snippets. Write a system prompt that: (1) sets role, (2) includes a placeholder for retrieved context, (3) refuses off-topic queries with a specific fallback, (4) constrains output format.",
        rubric: [
          "Includes role, task, and a {context} or [RETRIEVED_DOCS] placeholder",
          "Explicit off-topic refusal with fallback text",
          "Structured output format (e.g. brief answer + citation)",
        ],
        hint: "Combine everything: role + retrieval placeholder + guardrail + output shape.",
      },
      {
        id: "m5-recap",
        type: "recap",
        title: "Recap: Advanced Techniques",
        points: [
          "RAG lets prompts reason over YOUR data.",
          "Guardrails specify both what to do AND what not to do.",
          "Treat prompts like code: version, test, iterate.",
        ],
      },
    ],
  },
];

export const BADGES = [
  { id: "first-steps", label: "First Steps", desc: "Complete your first lesson." },
  { id: "First Draft", label: "First Draft", desc: "Finish Module 01 — Prompt Basics." },
  { id: "Pattern Setter", label: "Pattern Setter", desc: "Finish Module 02 — Zero/Few-Shot." },
  { id: "Reasoner", label: "Reasoner", desc: "Finish Module 03 — Chain-of-Thought." },
  { id: "Impersonator", label: "Impersonator", desc: "Finish Module 04 — Role & Persona." },
  { id: "Prompt Architect", label: "Prompt Architect", desc: "Finish Module 05 — Advanced Techniques." },
  { id: "streak-3", label: "3-Day Streak", desc: "Learn 3 days in a row." },
  { id: "sharp-shooter", label: "Sharp Shooter", desc: "Score 85+ on a scenario." },
];

export const XP_PER_LEVEL = 250;

export function computeLevel(xp) {
  const level = Math.floor(xp / XP_PER_LEVEL) + 1;
  const into = xp % XP_PER_LEVEL;
  const pct = Math.round((into / XP_PER_LEVEL) * 100);
  return { level, into, pct, needed: XP_PER_LEVEL };
}
